import React, { useState } from 'react';
import { Channel, ChannelFormData } from '../types/Channel';
import { getYouTubeRedirectUrl, getFaviconUrl } from '../utils/channelStorage';
import './ChannelCard.css';

interface ChannelCardProps {
  channel: Channel;
  isEditMode: boolean;
  onUpdate: (channelId: string, updatedData: ChannelFormData) => void;
  onDelete: (channelId: string) => void;
  isDraggable?: boolean;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, index: number) => void;
  onTouchStart?: (e: React.TouchEvent, index: number) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  index?: number;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  isEditMode,
  onUpdate,
  onDelete,
  isDraggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  index = 0
}) => {
  const [formData, setFormData] = useState<ChannelFormData>({
    displayName: channel.displayName,
    link: channel.link,
    iconUrl: channel.iconUrl
  });

  const [imageError, setImageError] = useState(false);

  const handleInputChange = (field: keyof ChannelFormData, value: string) => {
    // Auto-generate favicon when link changes
    if (field === 'link') {
      const newIconUrl = getFaviconUrl(value);
      setFormData(prev => ({ ...prev, [field]: value, iconUrl: newIconUrl }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.displayName.trim() && formData.link.trim()) {
      onUpdate(channel.id, formData);
    }
  };

  const handleChannelClick = () => {
    if (!isEditMode) {
      const redirectUrl = getYouTubeRedirectUrl(formData.link);
      window.open(redirectUrl, '_blank');
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  if (isEditMode) {
    return (
      <>
        <div className="channel-card editing">
          <form onSubmit={handleSubmit} className="edit-form">
            <div className="icon-section">
              <div className="current-icon">
                {!imageError ? (
                  <img 
                    src={formData.iconUrl} 
                    alt={formData.displayName}
                    onError={handleImageError}
                  />
                ) : (
                  <div className="icon-fallback">
                    {formData.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor={`name-${channel.id}`}>Display Name:</label>
              <input
                id={`name-${channel.id}`}
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor={`link-${channel.id}`}>Channel Link:</label>
              <input
                id={`link-${channel.id}`}
                type="url"
                value={formData.link}
                onChange={(e) => handleInputChange('link', e.target.value)}
                required
              />
            </div>

            {formData.link && formData.link !== channel.link && (
              <div className="icon-preview">
                <span>Updated Icon Preview:</span>
                <img 
                  src={formData.iconUrl} 
                  alt="Updated channel icon"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="save-btn">Save</button>
              <button 
                type="button" 
                className="delete-btn"
                onClick={() => onDelete(channel.id)}
              >
                Delete
              </button>
            </div>
          </form>
        </div>
      </>
    );
  }

  return (
    <div 
      className="channel-card" 
      onClick={handleChannelClick}
      draggable={isDraggable}
      onDragStart={isDraggable ? (e) => onDragStart?.(e, index) : undefined}
      onDragOver={isDraggable ? onDragOver : undefined}
      onDrop={isDraggable ? (e) => onDrop?.(e, index) : undefined}
      onTouchStart={isDraggable ? (e) => onTouchStart?.(e, index) : undefined}
      onTouchMove={isDraggable ? onTouchMove : undefined}
      onTouchEnd={isDraggable ? onTouchEnd : undefined}
    >
      <div className="channel-icon">
        {!imageError ? (
          <img 
            src={channel.iconUrl} 
            alt={channel.displayName}
            onError={handleImageError}
          />
        ) : (
          <div className="icon-fallback">
            {channel.displayName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="channel-name">{channel.displayName}</div>
    </div>
  );
};

export default ChannelCard;
